import api from './api';
import type {
  Token,
  TokenDetails,
  CreateTokenRequest,
  RefreshTokenResponse,
  UpdateTokenCodesRequest,
} from '../types';
import type { ProviderType } from '../types';

const computeExpiresInSeconds = (expiresAt?: string): number => {
  if (!expiresAt) return 0;
  const ms = Date.parse(expiresAt);
  if (Number.isNaN(ms)) return 0;
  const diffSeconds = Math.floor((ms - Date.now()) / 1000);
  return Math.max(0, diffSeconds);
};

export class TokenService {
  /**
   * Get all tokens for the current user
   */
  async getUserTokens(): Promise<Token[]> {
    const response = await api.get<any[]>('/TokenServer/ExternalTokens/List');

    // Map API response to Token interface
    // API returns ExternalTokenID (long), UserID, IdentityProviderID
    // Also includes nested IdentityProvider and ProviderTemplate
    return response.data.map((item: any) => ({
      id: String(item.externalTokenID || item.externalTokenId || item.ExternalTokenID),
      tokenCode: item.code || item.Code,
      providerId: String(item.identityProviderID || item.identityProviderId || item.IdentityProviderID),
      providerCode: item.identityProvider?.code || item.identityProvider?.Code || item.providerCode || item.ProviderCode,
      providerTemplateCode:
        item.identityProvider?.providerTemplate?.code ||
        item.identityProvider?.providerTemplate?.Code ||
        item.providerTemplateCode ||
        item.ProviderTemplateCode,
      providerName: item.identityProvider?.name || item.providerName || 'Unknown Provider',
      providerType: (item.identityProvider?.providerTemplate?.providerType || 'Unknown') as ProviderType,
      templateName: item.identityProvider?.providerTemplate?.name,
      providerIsActive: item.identityProvider?.isActive,
      providerTemplateIsActive: item.identityProvider?.providerTemplate?.isActive,
      expiresAt: item.expiresAt,
      // API doesn't reliably return expiresInSeconds; compute from expiresAt when missing.
      expiresInSeconds: item.expiresInSeconds ?? computeExpiresInSeconds(item.expiresAt),
      status: item.status,
      scope: item.scope,
      lastRefreshAt: item.lastRefreshAt,
      createdAt: item.createdDate || item.createdAt,
      // External user information from OAuth provider
      externalUserId: item.externalUserId,
      externalUserEmail: item.externalUserEmail,
      externalUserName: item.externalUserName,
      externalUserFamilyName: item.externalUserFamilyName,
    }));
  }

  /**
   * Get a specific token with decrypted access token
   */
  async getToken(tokenId: string): Promise<TokenDetails> {
    const response = await api.get<any>(`/TokenServer/ExternalTokens/${tokenId}/Decrypted`);

    // Map API response to TokenDetails
    const item = response.data;
    return {
      id: String(item.externalTokenID || item.externalTokenId || item.ExternalTokenID),
      providerId: String(item.identityProviderID || item.identityProviderId || item.IdentityProviderID),
      providerName: item.providerName || item.identityProvider?.name || 'Unknown',
      providerType: item.providerType || item.identityProvider?.providerType || 'Unknown',
      accessToken: item.accessToken,
      refreshToken: item.refreshToken,
      tokenType: item.tokenType,
      expiresAt: item.expiresAt,
      expiresInSeconds: item.expiresInSeconds || 0,
      status: item.status,
      scope: item.scope,
      lastRefreshAt: item.lastRefreshAt,
      createdAt: item.createdDate || item.createdAt || new Date().toISOString(),
    };
  }

  /**
   * Store a new token (after OAuth callback)
   */
  async createToken(data: CreateTokenRequest): Promise<Token> {
    const response = await api.post<Token>('/TokenServer/ExternalTokens/Create', data);
    return response.data;
  }

  /**
   * Manually refresh a token
   */
  async refreshToken(tokenId: string): Promise<RefreshTokenResponse> {
    const response = await api.post<RefreshTokenResponse>(`/TokenServer/ExternalTokens/${tokenId}/Refresh`);
    return response.data;
  }

  /**
   * Delete and revoke a token
   */
  async deleteToken(tokenId: string): Promise<void> {
    await api.delete(`/TokenServer/ExternalTokens/${tokenId}/Delete`);
  }

  /**
   * Update an existing token
   */
  async updateToken(tokenId: string, data: Partial<CreateTokenRequest>): Promise<Token> {
    const response = await api.put<Token>(`/TokenServer/ExternalTokens/${tokenId}/Update`, data);
    return response.data;
  }

  /**
   * Update token, provider, and template codes for an existing token
   */
  async updateTokenCodes(tokenId: string, data: UpdateTokenCodesRequest): Promise<void> {
    await api.put(`/TokenServer/ExternalTokens/${tokenId}/Codes`, data);
  }
}

// Export singleton instance
export const tokenService = new TokenService();
